/***************************************************************************************
 * FileName      : FlowEffectService.ts
 * Description   : 장비의 관을 따라 흐르는 물/공기 애니메이션을 3D 공간에 배치/제어
 *                 - 커스텀 mp.mediaFlow Scene Component 등록
 *                 - 흐름 하나가 '갈래(branch) 트리' 를 품는다: 본선에서 갈라져 나온
 *                   갈래는 분기점에서 시작하고, 분기점에서 본선 입자와 맞물리게 위상을 맞춘다
 *                 - 매질(물/공기)은 점 단위로 지정한다. 점 i 의 매질이 i→i+1 구간에 적용되어
 *                   한 배관 안에서 물 구간과 공기 구간이 섞일 수 있다
 *                 - 흐름 방향은 갈래 단위이고 그 갈래 구간에만 걸린다 (본선까지 거슬러 가지 않음)
 *                 - 서비스가 트리를 갈래별 경로(route) 목록으로 펼쳐 컴포넌트에 전달
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.08.28
 * Modified Date : 2026.08.31 matterport_mvp1/16-water-flow-effect 에서 포팅 —
 *                 배관 좌표/스타일은 assets/json/flow/*.json 에 두고 서비스는 그리기만 담당.
 *                 단위 프로젝트에 있던 편집용 API(갈래 추가·점 삽입 등)는 그대로 두어
 *                 mvp1 에서 재튜닝한 결과를 파일 교체만으로 다시 가져올 수 있게 한다.
 * Reference     : matterport_mvp1/src/16-water-flow-effect/services/FlowEffectService,
 *                 BubbleEffectService (커스텀 Scene Component 등록 패턴)
 ***************************************************************************************/

import { SceneComponent } from '@mp/common';

// ============================================================================
// 타입 정의
// ============================================================================

export interface Vec3 { x: number; y: number; z: number; }

/**
 * 흐르는 매질.
 *  - water: 입자가 경로를 따라 곧게 흐른다
 *  - air:   입자가 관 축을 중심으로 소용돌이치며 흐르고, 크기가 맥동한다
 */
export type FlowMedium = 'water' | 'air';

export interface FlowPoint extends Vec3 {
  /**
   * '이 점으로 들어오는' 구간(이전 점 → 이 점)의 매질. 생략하면 흐름의 기본 매질을 따른다.
   *
   * 들어오는 쪽을 기준으로 잡아야 갈래가 자기 점으로 자기 구간만 제어할 수 있다.
   * 갈래의 첫 점은 분기점에서 자기에게 오는 구간을 담당하므로, 점이 하나뿐인 갈래도
   * 매질을 지정할 수 있다. 본선의 첫 점(시작)만 앞에 구간이 없어 값이 쓰이지 않는다.
   */
  medium?: FlowMedium;
}

/**
 * 입자 모양.
 *  - sphere: 속이 찬 구 (물)
 *  - ring:   속이 빈 정원 (공기) — 회전이 필요 없어 스프라이트를 그대로 쓴다
 */
export type ParticleShape = 'sphere' | 'ring';

/** 매질별 표현 설정 — 같은 흐름 안에서 물 구간과 공기 구간이 다르게 그려진다 */
export interface FlowMediumStyle {
  /** 입자 모양. 기본 물은 sphere, 공기는 ring */
  shape: ParticleShape;
  /** 링 두께 (반지름 대비 비율 0.12~0.7). shape 가 ring 일 때만 쓰인다 */
  ringWidth: number;
  /**
   * 어두운 외곽선 세기 0~1.
   * 밝은 벽이든 어두운 장비든 배경 밝기와 무관하게 형태를 세워 준다.
   * 공기처럼 옅은 색이 배경에 묻히는 것을 막는 핵심 장치.
   */
  outline: number;
  /** 입자 반지름 (m) */
  radius: number;
  /** 경로 수직 방향 산포 (m) — 줄기 굵기 / 퍼짐 반경 */
  jitter: number;
  /** 입자 색상 (16진 RGB) */
  color: number;
  /**
   * 경로 가이드 라인 색상 (16진 RGB).
   * 입자 색과 따로 두는 이유는, 공기처럼 옅어야 하는 매질도 가이드는 진해야
   * 회색 벽 앞에서 보이기 때문이다.
   */
  guideColor: number;
  /** 불투명도 0~1 */
  opacity: number;
  /** 소용돌이 세기 (rad/m) — air 에서만 쓰인다 */
  swirl: number;
  /** 글로우 세기 0~1 — 입자 바깥으로 번지는 후광. 배경과 분리해 눈에 띄게 한다 */
  glow: number;
}

export interface BranchParent {
  /** 갈라져 나오는 부모 갈래의 id */
  branchId: string;
  /** 부모 갈래 waypoints 상의 분기점 인덱스 */
  pointIndex: number;
}

export interface FlowBranch {
  /** 흐름 안에서 유일한 갈래 식별자 */
  id: string;
  /** UI 표시용 라벨 (선택) */
  label?: string;
  /**
   * 이 갈래의 점들 (절대 월드 좌표).
   * 본선(root)은 시작점부터 담고, 자식 갈래는 분기점 '다음' 점부터 담는다.
   * (분기점 자체는 부모 갈래가 이미 가지고 있으므로 중복 저장하지 않는다)
   */
  waypoints: FlowPoint[];
  /** 없으면 본선(root). 있으면 부모 갈래의 어느 점에서 갈라지는지 */
  parent?: BranchParent;
  /**
   * 이 갈래가 만들어 내는 경로의 흐름 방향.
   * false(기본) 면 본선 시작 → 이 갈래 끝, true 면 이 갈래 끝 → 본선 시작(합류).
   * 갈래마다 따로 줄 수 있어 급수/환수처럼 본선을 공유하며 반대로 흐르는 배관을 표현할 수 있다.
   */
  reversed?: boolean;
}

export interface FlowConfig {
  /** flow 식별자 (update/remove/저장 키) */
  id: string;
  /** UI 표시용 라벨 (선택) */
  label?: string;
  /** branches[0] 은 반드시 본선(parent 없음) */
  branches: FlowBranch[];
  /** 매질이 지정되지 않은 점에 적용할 기본 매질. 기본 'water' */
  medium?: FlowMedium;
  /** 매질별 표현 설정 */
  styles?: Record<FlowMedium, FlowMediumStyle>;
  /** 입자 간 간격 (미터) — 흐름 전체 공통. 기본 0.14 */
  spacing?: number;
  /** 흐름 속도 (m/s) — 흐름 전체 공통. 기본 0.3 */
  speed?: number;
  /** 경로 가이드 라인 표시 — 입자 사이가 비어 있어도 관 경로가 보인다. 기본 true */
  guide?: boolean;
  /** 가이드 라인 반지름 (m). 기본 0.005 */
  guideWidth?: number;
  /**
   * 가이드 라인 뒤에 깔리는 어두운 테두리 세기 0~1. 기본 0 (없음).
   * 가이드 색을 진하게 잡는 것으로 충분하지만, 더 세워야 할 때 올린다.
   */
  guideOutline?: number;
  /**
   * true 면 depthTest 를 꺼서 시설물/벽에 가리지 않고 항상 보임.
   * 기본 false — 장비 뒤로 들어간 구간은 실제로 가려진다 (배치 작업 중에만 켜서 쓰는 용도).
   */
  alwaysOnTop?: boolean;
}

/**
 * 저장 JSON 하위호환 형식.
 *  - 분기 개념이 없던 시절: waypoints 단일 배열
 *  - 매질이 흐름 단위이던 시절: radius/jitter/color/opacity/swirl 이 흐름에 직접 붙어 있음
 */
interface LegacyFlowConfig {
  id: string;
  label?: string;
  branches?: FlowBranch[];
  waypoints?: FlowPoint[];
  medium?: FlowMedium;
  styles?: Record<FlowMedium, FlowMediumStyle>;
  spacing?: number;
  speed?: number;
  guide?: boolean;
  guideWidth?: number;
  guideOutline?: number;
  reversed?: boolean;
  alwaysOnTop?: boolean;
  radius?: number;
  jitter?: number;
  color?: number;
  opacity?: number;
  swirl?: number;
}

// ============================================================================
// 기본값
// ============================================================================

/**
 * 매질별 표현 기본값.
 *
 * 실제 장면에서 맞춰 본 값을 그대로 옮겨 둔 것이라, 새 흐름을 만들면
 * src/assets/flow-config.json 에 저장된 흐름과 같은 모습으로 시작한다.
 * 물과 공기는 크기·간격·직진성이 모두 같고, 속이 찼는지 비었는지로만 구분된다.
 */
export const MEDIUM_STYLES: Record<FlowMedium, FlowMediumStyle> = {
  water: {
    shape: 'sphere', ringWidth: 0.32, outline: 0,
    radius: 0.020, jitter: 0, color: 0x38bdf8, guideColor: 0x0284c7,
    opacity: 0.85, swirl: 0, glow: 1,
  },
  air: {
    // 입자는 옅어야 공기답지만 가이드까지 옅으면 회색 벽에 묻힌다 — 가이드만 진하게
    shape: 'ring', ringWidth: 0.32, outline: 0.85,
    radius: 0.020, jitter: 0, color: 0xe2e8f0, guideColor: 0x475569,
    opacity: 0.85, swirl: 0, glow: 1,
  },
};

/** 새 흐름을 만들 때 매질에 맞춰 잡아 주는 흐름 공통값 */
export const MEDIUM_FLOW_DEFAULTS: Record<FlowMedium, { spacing: number; speed: number }> = {
  water: { spacing: 0.14, speed: 0.3 },
  air:   { spacing: 0.14, speed: 0.3 },
};

const DEFAULTS = {
  medium: 'water' as FlowMedium,
  spacing: 0.14,
  speed: 0.3,
  guide: true,
  guideWidth: 0.005,
  // 가이드 테두리는 기본 끔 — 가이드 색을 진하게 잡는 쪽이 깔끔하다
  guideOutline: 0,
  alwaysOnTop: false,
};

/** 스프라이트 텍스처에서 물방울 몸통이 차지하는 반지름 비율 (나머지는 글로우 여백) */
const BODY_FRACTION = 0.30;

export const ROOT_BRANCH_ID = 'main';

/** 풀 폭주 방지 — 간격을 아주 작게 줘도 흐름 전체가 이 개수를 넘지 않음 */
const MAX_PARTICLES = 600;

/**
 * 겹쳐 그리는 순서. 값이 클수록 앞이다.
 *
 * 입자와 가이드는 같은 중심선 위에 있어 서로 z 가 거의 같다. 반투명이라
 * depthWrite 도 꺼 두었으므로 카메라 거리 정렬에 맡기면 각도에 따라 앞뒤가 뒤집힌다.
 * 그래서 순서를 값으로 못박는다 — 뒤에서부터 공기 가이드, 물 가이드, 입자.
 */
const LAYER = {
  guideBacking: { air: 0, water: 2 } as Record<FlowMedium, number>,
  guideCore: { air: 1, water: 3 } as Record<FlowMedium, number>,
  particle: { air: 10, water: 11 } as Record<FlowMedium, number>,
};

export const defaultStyles = (): Record<FlowMedium, FlowMediumStyle> => ({
  water: { ...MEDIUM_STYLES.water },
  air: { ...MEDIUM_STYLES.air },
});

// ============================================================================
// 커스텀 Scene Component (mp.mediaFlow)
// ============================================================================

const FLOW_COMPONENT_TYPE = 'mp.mediaFlow';

interface Particle {
  mesh: any;
  /** 이 입자가 배정된 route 인덱스 */
  routeIndex: number;
  /** route 시작점 기준 진행 거리 (m) */
  distance: number;
  /** 이 입자 고유의 경로 수직 오프셋 (줄기가 한 줄로 붙지 않게) */
  offsetU: number;
  offsetV: number;
  /** 크기 편차 계수 (0.7 ~ 1.3) */
  sizeFactor: number;
  /** 맥동 위상 — 공기 덩이가 제각각 부풀었다 줄어들게 (0 ~ 2π) */
  pulsePhase: number;
  /** 현재 적용 중인 매질 — 바뀔 때만 material 을 갈아 끼우기 위해 기억해 둔다 */
  appliedMedium: FlowMedium | null;
  /** 흐르는 중인지. false 면 풀에서 대기 중 */
  active: boolean;
}

/** 컴포넌트에 넘기는 경로 하나 (앵커 기준 상대 좌표) */
export interface RouteInput {
  points: Vec3[];
  /** mediums[i] = points[i] → points[i+1] 구간의 매질. 길이는 points.length - 1 */
  mediums: FlowMedium[];
  /** true 면 이 경로만 거꾸로 흐른다 */
  reversed: boolean;
  /**
   * 본선 시작점에서 이 경로의 시작점(=분기점)까지의 거리.
   * 갈래 스트림의 스폰 위상을 본선에 맞춰, 분기점에서 입자가 어긋나지 않게 하는 데 쓴다.
   */
  startOffset: number;
}

/** 펼쳐진 경로 하나 — 누적 길이표와 세그먼트별 수직 기저를 미리 계산해 둔다 */
interface RoutePath {
  points: Vec3[];
  mediums: FlowMedium[];
  reversed: boolean;
  startOffset: number;
  /** cumulative[i] = points[0]..points[i] 까지의 길이 */
  cumulative: number[];
  basisU: Vec3[];
  basisV: Vec3[];
  length: number;
}

interface FlowInputs {
  /**
   * 앵커(본선 시작점) 기준 상대 좌표로 펼쳐진 경로 목록.
   * 분기가 있으면 여러 경로가 앞부분(본선)을 공유한다.
   */
  routes: RouteInput[];
  styles: Record<FlowMedium, FlowMediumStyle>;
  spacing: number;
  speed: number;
  alwaysOnTop: boolean;
  guide: boolean;
  guideWidth: number;
  guideOutline: number;
}

/**
 * 입자 스프라이트 텍스처를 굽는다.
 *
 * 바깥으로 번지는 글로우 → 어두운 외곽선 → 몸통 → 안쪽 빛 순서로 겹친다.
 * 외곽선은 배경 밝기와 무관하게 형태를 세워 주므로, 공기처럼 옅은 색이
 * 회색 벽 앞에서 사라지는 것을 막는다.
 *
 * 링은 정원이라 진행 방향으로 회전할 필요가 없다 — 그래서 평면 메시로 바꾸지 않고
 * 스프라이트를 그대로 쓸 수 있다. 스타일이 바뀔 때만 다시 굽는다.
 */
function makeParticleTexture(THREE: any, style: FlowMediumStyle): any {
  const SIZE = 128;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const g = canvas.getContext('2d')!;

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const bodyR = SIZE * BODY_FRACTION;

  const r = (style.color >> 16) & 255;
  const gg = (style.color >> 8) & 255;
  const b = style.color & 255;
  const tint = (a: number) => `rgba(${r},${gg},${b},${a})`;
  const ink = (a: number) => `rgba(14,20,26,${a})`;

  // 1) 글로우 — 몸통 바깥으로 부드럽게 번지는 후광
  if (style.glow > 0) {
    const halo = g.createRadialGradient(cx, cy, bodyR * 0.75, cx, cy, SIZE * 0.5);
    halo.addColorStop(0, tint(0.55 * style.glow));
    halo.addColorStop(0.42, tint(0.16 * style.glow));
    halo.addColorStop(1, tint(0));
    g.fillStyle = halo;
    g.beginPath();
    g.arc(cx, cy, SIZE * 0.5, 0, Math.PI * 2);
    g.fill();
  }

  if (style.shape === 'ring') {
    const lw = Math.max(2, bodyR * style.ringWidth);

    // 2) 어두운 외곽선 — 링보다 두껍게 깔아 안팎 양쪽에 테두리를 남긴다
    if (style.outline > 0) {
      g.strokeStyle = ink(0.85 * style.outline);
      g.lineWidth = lw + bodyR * 0.30;
      g.beginPath();
      g.arc(cx, cy, bodyR, 0, Math.PI * 2);
      g.stroke();
    }

    // 3) 링 본체
    g.strokeStyle = tint(0.96);
    g.lineWidth = lw;
    g.beginPath();
    g.arc(cx, cy, bodyR, 0, Math.PI * 2);
    g.stroke();

    // 4) 안쪽 빛 — 좌상단 호를 살짝 밝혀 입체감을 준다
    g.strokeStyle = 'rgba(255,255,255,0.55)';
    g.lineWidth = lw * 0.34;
    g.beginPath();
    g.arc(cx, cy, bodyR - lw * 0.24, Math.PI * 1.02, Math.PI * 1.72);
    g.stroke();

  } else {
    // 2) 어두운 외곽선
    if (style.outline > 0) {
      g.fillStyle = ink(0.85 * style.outline);
      g.beginPath();
      g.arc(cx, cy, bodyR * 1.16, 0, Math.PI * 2);
      g.fill();
    }

    // 3) 몸통
    g.fillStyle = tint(0.92);
    g.beginPath();
    g.arc(cx, cy, bodyR, 0, Math.PI * 2);
    g.fill();

    // 4) 안쪽 빛 — 몸통 원 안으로만 칠해 볼륨감을 준다
    const hi = g.createRadialGradient(
      cx - bodyR * 0.32, cy - bodyR * 0.34, 0,
      cx - bodyR * 0.32, cy - bodyR * 0.34, bodyR * 0.62,
    );
    hi.addColorStop(0, 'rgba(255,255,255,0.88)');
    hi.addColorStop(0.55, 'rgba(255,255,255,0.22)');
    hi.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = hi;
    g.beginPath();
    g.arc(cx, cy, bodyR, 0, Math.PI * 2);
    g.fill();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

class MediaFlowComponent extends SceneComponent {
  inputs: FlowInputs = {
    routes: [],
    styles: defaultStyles(),
    spacing: DEFAULTS.spacing,
    speed: DEFAULTS.speed,
    alwaysOnTop: DEFAULTS.alwaysOnTop,
    guide: DEFAULTS.guide,
    guideWidth: DEFAULTS.guideWidth,
    guideOutline: DEFAULTS.guideOutline,
  };

  private root: any = null;
  private particles: Particle[] = [];
  /** 매질마다 스프라이트 재질/텍스처를 하나씩 — 한 흐름 안에서 구간별로 갈아 끼운다 */
  private materials: Record<FlowMedium, any> = { water: null, air: null };
  private textures: Record<FlowMedium, any> = { water: null, air: null };
  /** 텍스처를 다시 구울지 판단하는 시그니처 (색 + 글로우) */
  private textureSignature: Record<FlowMedium, string> = { water: '', air: '' };

  /** 경로 가이드 라인 */
  private guideGroup: any = null;
  private guideGeometry: any = null;
  private guideMaterials: Record<FlowMedium, any> = { water: null, air: null };
  /** 가이드 라인 뒤에 깔아 배경과 분리해 주는 어두운 실린더용 재질 */
  private guideBackingMaterial: any = null;
  private lastGuideCount = -1;
  /** 경로가 새로 만들어질 때마다 증가 — 가이드 재생성 판단용 */
  private pathsRevision = 0;
  private guideSignature = '';

  private lastTickMs = 0;
  private paths: RoutePath[] = [];

  // --- 스폰 상태 -------------------------------------------------------------
  // 입자를 고정 격자에 얹어 순환시키면 경로 길이가 간격의 배수가 아닐 때 한 바퀴의
  // 이음매에서 간격이 무너져 '두 개씩 붙은' 입자가 계속 돌아다닌다.
  // 그래서 출발점에서 일정 거리마다 하나씩 흘려보내고 끝에 닿으면 회수하는 방식을 쓴다.
  //
  // 경로는 갈래 하나당 하나이고 서로 겹치지 않는다(갈래 경로는 분기점에서 시작한다).
  // 갈래 스트림의 스폰 위상을 startOffset 에 맞춰 두면 분기점에서 본선 입자와 정확히
  // 이어져, 어디서나 간격이 s 로 유지되면서도 흐름이 끊겨 보이지 않는다.
  private acc: number[] = [];
  /**
   * 스트림이 시작된 뒤 흘러간 거리 (m).
   * 갈래는 본선의 첫 입자가 분기점에 닿은 뒤에 출발해야 관이 순서대로 채워진다.
   */
  private travelled = 0;
  /** 경로별로 이미 흘려보내기 시작했는지 */
  private started: boolean[] = [];
  /**
   * 한 번이라도 채워진 적이 있는지.
   * 처음 만들어질 때는 비어 있는 관이 시작점부터 차례로 채워지는 게 보여야 하고,
   * 점을 옮기는 등으로 다시 만들 때는 이미 흐르던 상태가 끊기면 안 된다.
   */
  private hasFilled = false;
  /** 실제 적용 간격 — 입자 상한을 넘으면 요청 간격보다 넓어진다 */
  private effSpacing = DEFAULTS.spacing;
  /** 재구성 여부 판단용 — 경로 길이/방향 + 간격 시그니처 */
  private lastBuildSignature = '';

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;

    this.guideGroup = new THREE.Object3D();
    this.root.add(this.guideGroup);
    // 단위 실린더(반지름 1, 높이 1)를 세그먼트마다 늘려서 재사용한다
    this.guideGeometry = new THREE.CylinderGeometry(1, 1, 1, 8, 1, true);

    for (const medium of ['water', 'air'] as const) {
      this.materials[medium] = new THREE.SpriteMaterial({
        transparent: true,
        depthWrite: false,
        // depthTest 켜짐(기본) → 스캔 메시의 깊이값과 비교되어 장비/벽 뒤 구간은 가려진다.
        // alwaysOnTop 은 배치 작업 중 경로 전체를 눈으로 확인할 때만 켜는 용도.
        depthTest: !this.inputs.alwaysOnTop,
      });
      this.guideMaterials[medium] = new THREE.MeshBasicMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: !this.inputs.alwaysOnTop,
      });
    }

    // 3D 에서 굵은 실린더를 겹쳐 두면 '뒤에 깔리는' 게 아니라 밝은 선을 감싸 버린다.
    // BackSide 로 뒷면만 그리면 어느 각도에서 봐도 밝은 선 뒤쪽에만 남아 테두리가 된다.
    this.guideBackingMaterial = new THREE.MeshBasicMaterial({
      color: 0x0e141a,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: !this.inputs.alwaysOnTop,
    });

    this.syncMaterials();
    this.rebuild();
    this.maybeRebuildGuide();
  }

  onInputsUpdated(_prev: FlowInputs) {
    this.syncMaterials();
    // 경로/방향/간격이 바뀌었을 때만 스트림을 다시 만든다.
    // (속도·크기는 onTick 에서 매 프레임 참조하므로 재구성 불필요)
    this.rebuild();
    this.maybeRebuildGuide();
  }

  /** 색·글로우·불투명도·가림 설정을 재질에 반영. 텍스처는 필요할 때만 다시 굽는다. */
  private syncMaterials() {
    const THREE = this.context.three;
    const wantDepthTest = !this.inputs.alwaysOnTop;

    for (const medium of ['water', 'air'] as const) {
      const style = this.inputs.styles[medium];
      const mat = this.materials[medium];
      if (!mat) continue;

      const signature = `${style.shape}|${style.color}|${style.glow}|${style.ringWidth}|${style.outline}`;
      if (signature !== this.textureSignature[medium]) {
        this.textureSignature[medium] = signature;
        if (this.textures[medium]) this.textures[medium].dispose();
        this.textures[medium] = makeParticleTexture(THREE, style);
        mat.map = this.textures[medium];
        mat.needsUpdate = true;
        console.log(
          `[mp.mediaFlow] ${medium} 텍스처 재생성 — ${style.shape}, 색 #${(style.color >>> 0).toString(16).padStart(6, '0')}, 글로우 ${style.glow}`,
        );
      }

      mat.opacity = style.opacity;
      if (mat.depthTest !== wantDepthTest) {
        mat.depthTest = wantDepthTest;
        mat.needsUpdate = true;
      }

      if (this.guideBackingMaterial) {
        this.guideBackingMaterial.opacity = this.inputs.guideOutline;
        if (this.guideBackingMaterial.depthTest !== wantDepthTest) {
          this.guideBackingMaterial.depthTest = wantDepthTest;
          this.guideBackingMaterial.needsUpdate = true;
        }
      }

      const guideMat = this.guideMaterials[medium];
      if (guideMat) {
        guideMat.color.setHex(style.guideColor);
        guideMat.opacity = Math.min(1, style.opacity * 0.9);
        if (guideMat.depthTest !== wantDepthTest) {
          guideMat.depthTest = wantDepthTest;
          guideMat.needsUpdate = true;
        }
      }
    }
  }

  onTick(_deltaMs: number) {
    // 자체 clock 으로 dt 산출 (외부 deltaMs 는 환경마다 의미가 달라 신뢰 불가)
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (this.lastTickMs === 0) {
      this.lastTickMs = now;
      return;
    }
    const dt = Math.min(0.1, (now - this.lastTickMs) / 1000); // 탭 비활성 후 점프 방지
    this.lastTickMs = now;

    // 색·글로우·불투명도는 매 프레임 확인해서 반영한다.
    // onInputsUpdated 만 믿으면 스타일만 바뀌었을 때 콜백이 걸리지 않아 변경이 묻힌다.
    // 시그니처가 같으면 문자열 비교 두 번으로 끝나므로 부담이 없다.
    this.syncMaterials();
    this.maybeRebuildGuide();

    if (dt <= 0 || this.paths.length === 0) return;

    const advance = this.inputs.speed * dt;
    const s = this.effSpacing;

    // 1) 스폰 — 경로마다 진행 거리 s 마다 하나씩. 누적 잔여분만큼 이미 나아간 상태로
    //    내보내 프레임 길이에 따라 간격이 들쭉날쭉해지는 것을 막는다.
    this.travelled += advance;
    for (let r = 0; r < this.paths.length; r++) {
      const path = this.paths[r];

      if (!this.started[r]) {
        // 갈래는 흐름이 자기 분기점에 닿은 뒤에 출발한다 — 그래야 첫 재생에서
        // 관이 시작점부터 분기점, 갈래 끝 순서로 차례로 채워진다.
        // 합류(역방향) 갈래도 같은 배관망의 분기이므로 똑같이 기다린다.
        // 흐르는 방향만 반대라, 분기점에 닿은 시각부터 자기 끝에서 되돌아 흐르기 시작한다.
        if (this.travelled < path.startOffset) continue;
        this.started[r] = true;
        // 문턱을 넘은 만큼은 이미 나아간 것으로 쳐서 첫 입자가 제 위치에서 출발하게 한다
        this.acc[r] = s + (this.travelled - path.startOffset);
      } else {
        this.acc[r] += advance;
      }

      while (this.acc[r] >= s) {
        this.acc[r] -= s;
        this.spawn(r, this.acc[r]);
      }
    }

    // 2) 이동 — 경로 끝에 닿으면 회수해서 풀로 돌려보낸다
    for (const p of this.particles) {
      if (!p.active) continue;
      const path = this.paths[p.routeIndex];
      if (!path || path.length <= 0) {
        this.retire(p);
        continue;
      }

      p.distance += advance;
      if (p.distance >= path.length) {
        this.retire(p);
        continue;
      }

      // 역방향 갈래는 자기 끝에서 분기점 쪽으로 흐른다 — 진행거리를 뒤집어 읽는다.
      // 경로가 갈래 고유 구간만 담으므로 본선까지 거슬러 올라가지 않는다.
      const sPos = path.reversed ? path.length - p.distance : p.distance;

      const seg = locate(path, sPos);
      if (!seg) continue;
      p.mesh.visible = true;

      // 지금 지나고 있는 구간의 매질에 따라 재질과 크기/퍼짐/소용돌이가 정해진다
      const medium = path.mediums[seg.index] ?? 'water';
      if (p.appliedMedium !== medium) {
        p.mesh.material = this.materials[medium];
        p.mesh.renderOrder = LAYER.particle[medium];
        p.appliedMedium = medium;
      }
      const style = this.inputs.styles[medium];
      const swirl = style.swirl;

      // 퍼짐이 0 이면 경로 중심선을 그대로 따라 일직선으로 나아간다
      let px = seg.x;
      let py = seg.y;
      let pz = seg.z;
      if (style.jitter > 0) {
        const u = path.basisU[seg.index];
        const v = path.basisV[seg.index];

        // 공기는 관 축을 중심으로 소용돌이치며 나아간다 — 오프셋을 진행거리에 비례해 회전
        let cu = p.offsetU;
        let cv = p.offsetV;
        if (swirl !== 0) {
          const ang = p.distance * swirl + p.pulsePhase;
          const cos = Math.cos(ang);
          const sin = Math.sin(ang);
          cu = p.offsetU * cos - p.offsetV * sin;
          cv = p.offsetU * sin + p.offsetV * cos;
        }
        px += (u.x * cu + v.x * cv) * style.jitter;
        py += (u.y * cu + v.y * cv) * style.jitter;
        pz += (u.z * cu + v.z * cv) * style.jitter;
      }
      p.mesh.position.set(px, py, pz);

      // 스프라이트에서 몸통은 텍스처의 일부만 차지하므로 그만큼 키워야 반지름이 맞는다
      const size = (style.radius * p.sizeFactor) / BODY_FRACTION;
      p.mesh.scale.set(size, size, 1);
    }
  }

  onDestroy() {
    for (const medium of ['water', 'air'] as const) {
      if (this.materials[medium]) this.materials[medium].dispose();
      if (this.textures[medium]) this.textures[medium].dispose();
      if (this.guideMaterials[medium]) this.guideMaterials[medium].dispose();
    }
    if (this.guideBackingMaterial) this.guideBackingMaterial.dispose();
    if (this.guideGeometry) this.guideGeometry.dispose();
    this.particles = [];
    this.paths = [];
  }

  // --------------------------------------------------------------------------
  // 경로 가이드 라인
  // --------------------------------------------------------------------------

  /**
   * 배관 경로를 가는 실린더로 깔아 둔다. 입자 사이가 비어 있어도 관이 어디를 지나는지
   * 보이므로 점을 맞추는 작업이 훨씬 수월해진다.
   * 경로들이 본선을 공유하므로 같은 세그먼트는 한 번만 그린다.
   */
  /**
   * 가이드 설정이나 경로가 바뀌었을 때만 다시 세운다.
   * 색과 마찬가지로 onInputsUpdated 만 믿지 않도록 onTick 에서도 확인한다.
   */
  private maybeRebuildGuide() {
    const signature =
      `${this.inputs.guide}|${this.inputs.guideWidth}|${this.inputs.guideOutline > 0}|${this.pathsRevision}`;
    if (signature === this.guideSignature) return;
    this.guideSignature = signature;
    this.rebuildGuide();
  }

  private rebuildGuide() {
    const THREE = this.context.three;
    if (!this.guideGroup) return;

    for (let i = this.guideGroup.children.length - 1; i >= 0; i--) {
      this.guideGroup.remove(this.guideGroup.children[i]);
    }
    if (!this.inputs.guide) return;

    const width = Math.max(0.001, this.inputs.guideWidth);
    const seen: Record<string, boolean> = {};
    const key = (p: Vec3) => `${p.x.toFixed(4)},${p.y.toFixed(4)},${p.z.toFixed(4)}`;
    const up = new THREE.Vector3(0, 1, 0);

    for (const path of this.paths) {
      for (let i = 0; i < path.points.length - 1; i++) {
        const a = path.points[i];
        const b = path.points[i + 1];
        const id = key(a) + '>' + key(b);
        if (seen[id]) continue;
        seen[id] = true;

        const dir = new THREE.Vector3(b.x - a.x, b.y - a.y, b.z - a.z);
        const len = dir.length();
        if (len < 1e-5) continue;

        const medium = path.mediums[i] ?? 'water';
        dir.normalize();
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 };

        // 어두운 테두리를 먼저 깔아 밝은 벽 앞에서도 가이드가 살아남게 한다
        if (this.inputs.guideOutline > 0) {
          const backing = new THREE.Mesh(this.guideGeometry, this.guideBackingMaterial);
          backing.position.set(mid.x, mid.y, mid.z);
          backing.scale.set(width * 1.8, len, width * 1.8);
          backing.quaternion.setFromUnitVectors(up, dir);
          backing.renderOrder = LAYER.guideBacking[medium];
          this.guideGroup.add(backing);
        }

        const mesh = new THREE.Mesh(this.guideGeometry, this.guideMaterials[medium]);
        mesh.position.set(mid.x, mid.y, mid.z);
        mesh.scale.set(width, len, width);
        mesh.quaternion.setFromUnitVectors(up, dir);
        mesh.renderOrder = LAYER.guideCore[medium];
        this.guideGroup.add(mesh);
      }
    }

    // 가이드가 안 보일 때 '만들어지긴 했는지' 를 콘솔에서 바로 가릴 수 있게 한 번만 남긴다
    // (세그먼트마다 배경선 + 본선 두 개씩이라 개수는 구간 수의 두 배다)
    const count = this.guideGroup.children.length;
    if (count !== this.lastGuideCount) {
      this.lastGuideCount = count;
      console.log(`[mp.mediaFlow] 가이드 라인 세그먼트 ${count}개 (굵기 ${width}m)`);
    }
  }

  // --------------------------------------------------------------------------
  // 스트림 재구성
  // --------------------------------------------------------------------------

  private rebuild() {
    const paths = (this.inputs.routes || [])
      .filter(r => r && r.points && r.points.length >= 2)
      .map(buildRoutePath);

    // 좌표는 항상 갱신한다. 점을 옮겨도 길이 합이 그대로인 경우(직선 위에서 중간점 이동 등)가
    // 있어서, 아래 시그니처만 보고 건너뛰면 모양이 옛 좌표에 멈춰 버린다.
    this.paths = paths;
    this.pathsRevision += 1;

    const spacing = Math.max(0.01, this.inputs.spacing);
    const signature =
      paths.map(p => p.length.toFixed(4) + (p.reversed ? ':r' : ':f')).join(',') +
      '|' + spacing.toFixed(4);
    // 길이·방향·간격이 그대로면 흐르던 입자를 그대로 두고 좌표만 새 경로를 따르게 한다
    if (signature === this.lastBuildSignature) return;
    this.lastBuildSignature = signature;

    const totalLength = paths.reduce((sum, p) => sum + p.length, 0);
    // 상한을 넘으면 간격을 넓혀 전체 개수를 눌러 준다
    const budget = Math.max(1, MAX_PARTICLES - 8);
    this.effSpacing = totalLength > 0 ? Math.max(spacing, totalLength / budget) : spacing;

    // 갈래 스트림의 스폰 위상 — 분기점에서 본선 입자와 맞물리도록 startOffset 만큼 어긋낸다
    this.acc = paths.map(p => phaseFor(p.startOffset, this.effSpacing));
    this.started = paths.map(() => false);
    this.travelled = 0;

    this.resizePool(
      totalLength > 0
        ? Math.min(MAX_PARTICLES, Math.ceil(totalLength / this.effSpacing) + paths.length + 8)
        : 0,
    );

    // 처음 만들어질 때만 빈 관에서 시작한다. 점을 옮기는 등으로 다시 만들 때는
    // 정상 상태를 즉시 채워, 조정할 때마다 관이 비었다 차오르는 일이 없게 한다.
    if (this.hasFilled) {
      this.prefill();
    } else {
      this.hasFilled = true;
    }
  }

  private resizePool(wanted: number) {
    const THREE = this.context.three;
    while (this.particles.length > wanted) {
      const p = this.particles.pop();
      if (p) this.root.remove(p.mesh);
    }
    while (this.particles.length < wanted) {
      const sprite = new THREE.Sprite(this.materials.water);
      sprite.renderOrder = LAYER.particle.water;
      sprite.visible = false;
      this.root.add(sprite);
      this.particles.push({
        mesh: sprite,
        routeIndex: 0,
        distance: 0,
        offsetU: 0,
        offsetV: 0,
        sizeFactor: 1,
        pulsePhase: 0,
        appliedMedium: 'water',
        active: false,
      });
    }
    // 경로가 바뀌었으므로 흐르던 입자는 전부 회수하고 아래 prefill 로 다시 채운다
    for (const p of this.particles) this.retire(p);
  }

  /**
   * 정상 상태를 즉시 만들어 둔다 — 점을 하나 옮길 때마다 관이 빈 채로
   * 몇 초씩 다시 차오르길 기다리지 않도록.
   * 지금부터 시간을 되감아 이미 내보냈을 입자들을 그대로 배치한다.
   */
  private prefill() {
    const s = this.effSpacing;
    for (let r = 0; r < this.paths.length; r++) {
      const start = this.acc[r];
      const len = this.paths[r].length;
      for (let d = start; d < len; d += s) this.spawn(r, d);
      // 이미 가득 찬 상태이므로 출발 대기 없이 이어서 흘려보낸다
      this.started[r] = true;
    }
  }

  /** 놀고 있는 입자를 하나 꺼내 경로 routeIndex 의 distance 지점에 띄운다 */
  private spawn(routeIndex: number, distance: number): void {
    const path = this.paths[routeIndex];
    if (!path || distance >= path.length) return;

    const p = this.particles.find(x => !x.active);
    if (!p) return; // 풀이 가득 차면 이번 스폰은 건너뛴다

    p.active = true;
    p.routeIndex = routeIndex;
    p.distance = distance;
    p.offsetU = Math.random() * 2 - 1;
    p.offsetV = Math.random() * 2 - 1;
    p.sizeFactor = 1;
    p.pulsePhase = Math.random() * Math.PI * 2;
    p.mesh.visible = true;
  }

  private retire(p: Particle) {
    p.active = false;
    p.mesh.visible = false;
  }
}

/**
 * 스폰 위상 — 경로 시작점이 본선 시작에서 startOffset 만큼 떨어져 있을 때,
 * 이 값에서 누적을 시작하면 분기점에서 본선 입자와 정확히 맞물린다.
 */
function phaseFor(startOffset: number, spacing: number): number {
  if (spacing <= 0) return 0;
  return (spacing - (startOffset % spacing)) % spacing;
}

// --- 경로 계산 --------------------------------------------------------------

/** 점 배열로부터 누적 길이표와 세그먼트별 수직 기저를 만든다 */
function buildRoutePath(route: RouteInput): RoutePath {
  const points = route.points;
  const cumulative: number[] = [0];
  const basisU: Vec3[] = [];
  const basisV: Vec3[] = [];
  let acc = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    acc += len;
    cumulative.push(acc);

    // 세그먼트 방향에 직교하는 두 축 — 임의의 up 과 외적해서 얻는다
    const dir = len > 1e-6
      ? { x: dx / len, y: dy / len, z: dz / len }
      : { x: 1, y: 0, z: 0 };
    // dir 이 Y축과 거의 평행하면 up 을 X축으로 바꿔 외적이 0벡터가 되는 것을 피함
    const up = Math.abs(dir.y) > 0.99 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
    const u = normalize(cross(dir, up));
    const v = normalize(cross(dir, u));
    basisU.push(u);
    basisV.push(v);
  }

  return {
    points, mediums: route.mediums, reversed: route.reversed,
    startOffset: route.startOffset, cumulative, basisU, basisV, length: acc,
  };
}

/**
 * 진행거리 s 에 해당하는 경로상의 좌표와 세그먼트 인덱스를 반환.
 * 누적길이표를 이진 탐색해 세그먼트를 찾고 그 안에서 선형보간한다.
 */
function locate(path: RoutePath, s: number): { x: number; y: number; z: number; index: number } | null {
  if (path.points.length < 2 || path.length <= 0) return null;

  const clamped = Math.max(0, Math.min(path.length, s));

  // cumulative[i] <= clamped < cumulative[i+1] 인 i 찾기
  let lo = 0;
  let hi = path.cumulative.length - 2; // 마지막 세그먼트 인덱스
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (path.cumulative[mid] <= clamped) lo = mid;
    else hi = mid - 1;
  }

  const a = path.points[lo];
  const b = path.points[lo + 1];
  const segLen = path.cumulative[lo + 1] - path.cumulative[lo];
  const t = segLen > 1e-6 ? (clamped - path.cumulative[lo]) / segLen : 0;

  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    index: lo,
  };
}

// --- 벡터 유틸 ---------------------------------------------------------------

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (len < 1e-6) return { x: 0, y: 1, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

const mediaFlowFactory = () => new MediaFlowComponent();

// ============================================================================
// 갈래 트리 → 경로 목록
// ============================================================================

export interface ExpandedRoute {
  /** 이 경로를 만들어 낸 갈래 (방향 설정의 주인) */
  branchId: string;
  /** 갈래가 차지하는 구간의 점들. 자식 갈래는 분기점부터 시작한다 */
  points: FlowPoint[];
  reversed: boolean;
  /** 본선 시작점에서 이 경로의 시작점까지의 거리 — 스폰 위상 맞춤용 */
  startOffset: number;
}

/**
 * 갈래 트리를 입자가 실제로 지나는 경로들로 펼친다 (절대 좌표, 매질 정보 포함).
 *
 * 경로는 '갈래 하나당 하나' 이고, 그 갈래가 실제로 차지하는 구간만 담는다.
 *  - 본선  : 시작점 → 본선 끝
 *  - 갈래  : 분기점 → 갈래 끝 (분기점 한 점만 부모에서 가져와 이어 붙인다)
 *
 * 갈래 경로가 본선까지 거슬러 올라가지 않으므로, 갈래의 방향을 뒤집어도 그 갈래
 * 구간만 거꾸로 흐른다. 경로끼리 세그먼트가 겹치지 않아 같은 자리에 입자가 두 겹으로
 * 얹히는 일도 없다.
 */
export function expandRoutes(branches: FlowBranch[]): ExpandedRoute[] {
  const byId = new Map(branches.map(b => [b.id, b]));

  /** 부모 waypoints 상의 분기점 인덱스 (범위를 벗어나면 잘라 준다) */
  const junctionIndex = (parent: FlowBranch, raw: number) =>
    Math.max(0, Math.min(raw, parent.waypoints.length - 1));

  /** 갈래가 실제로 그리는 점들 — 자식은 분기점부터 이어 붙여 부모와 끊기지 않게 한다 */
  const pointsOf = (branch: FlowBranch): FlowPoint[] => {
    if (!branch.parent) return branch.waypoints;
    const parent = byId.get(branch.parent.branchId);
    if (!parent || parent.waypoints.length === 0) return branch.waypoints;
    return [parent.waypoints[junctionIndex(parent, branch.parent.pointIndex)], ...branch.waypoints];
  };

  /** 본선 시작점부터 이 갈래의 시작점(분기점)까지의 거리 — 스폰 위상 맞춤용 */
  const offsets = new Map<string, number>();
  const offsetOf = (branch: FlowBranch, seen: Set<string>): number => {
    const cached = offsets.get(branch.id);
    if (cached !== undefined) return cached;

    let value = 0;
    const parent = branch.parent ? byId.get(branch.parent.branchId) : undefined;
    // 부모가 없거나 순환 참조면 0 으로 두고 끊는다 (잘못된 설정이 무한 재귀가 되지 않게)
    if (parent && !seen.has(parent.id)) {
      seen.add(parent.id);
      const parentPts = pointsOf(parent);
      // 부모 경로에서 분기점이 놓인 위치 — 자식 갈래는 부모의 분기점 한 점을 앞에 달고 있다
      const idx = junctionIndex(parent, branch.parent!.pointIndex) + (parent.parent ? 1 : 0);
      value = offsetOf(parent, seen) + polylineLength(parentPts, idx);
    }
    offsets.set(branch.id, value);
    return value;
  };

  const routes: ExpandedRoute[] = [];
  for (const b of branches) {
    const points = pointsOf(b);
    if (points.length < 2) continue;
    routes.push({
      branchId: b.id,
      points,
      reversed: b.reversed ?? false,
      startOffset: offsetOf(b, new Set([b.id])),
    });
  }
  return routes;
}

/** 점 배열의 0번부터 upto 번 점까지의 길이 */
function polylineLength(points: Vec3[], upto: number): number {
  let sum = 0;
  for (let i = 0; i < Math.min(upto, points.length - 1); i++) {
    const a = points[i];
    const b = points[i + 1];
    sum += Math.sqrt(
      (b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y) + (b.z - a.z) * (b.z - a.z),
    );
  }
  return sum;
}

/** 저장 JSON 을 현재 형식으로 정규화 — 옛 형식(단일 waypoints / 흐름 단위 매질)도 받아 준다 */
export function normalizeConfig(raw: LegacyFlowConfig): FlowConfig {
  const medium: FlowMedium = raw.medium ?? DEFAULTS.medium;

  const rawBranches: FlowBranch[] = raw.branches && raw.branches.length > 0
    ? raw.branches
    : [{ id: ROOT_BRANCH_ID, label: '본선', waypoints: raw.waypoints ?? [] }];

  // 방향이 흐름 단위이던 시절의 reversed 는 모든 갈래로 내려 준다
  const branches = rawBranches.map(b => ({
    ...b,
    reversed: b.reversed ?? raw.reversed ?? false,
  }));

  // 기본값에 저장값을 덮어써 예전 JSON 에 없던 필드(glow 등)를 채운다
  const base = defaultStyles();
  const styles: Record<FlowMedium, FlowMediumStyle> = {
    water: { ...base.water, ...(raw.styles ? raw.styles.water : null) },
    air: { ...base.air, ...(raw.styles ? raw.styles.air : null) },
  };
  // styles 가 없던 시절의 흐름 단위 파라미터를 해당 매질 style 로 옮겨 담는다
  if (!raw.styles) {
    const legacy = styles[medium];
    if (typeof raw.radius === 'number') legacy.radius = raw.radius;
    if (typeof raw.jitter === 'number') legacy.jitter = raw.jitter;
    if (typeof raw.color === 'number') legacy.color = raw.color;
    if (typeof raw.opacity === 'number') legacy.opacity = raw.opacity;
    if (typeof raw.swirl === 'number') legacy.swirl = raw.swirl;
  }

  return {
    id: raw.id,
    label: raw.label,
    branches,
    medium,
    styles,
    spacing: raw.spacing ?? MEDIUM_FLOW_DEFAULTS[medium].spacing,
    speed: raw.speed ?? MEDIUM_FLOW_DEFAULTS[medium].speed,
    guide: raw.guide ?? DEFAULTS.guide,
    guideWidth: raw.guideWidth ?? DEFAULTS.guideWidth,
    guideOutline: raw.guideOutline ?? DEFAULTS.guideOutline,
    alwaysOnTop: raw.alwaysOnTop ?? DEFAULTS.alwaysOnTop,
  };
}

/** 흐름 안에 실제로 쓰이고 있는 매질들 (UI 표시용) */
export function usedMediums(config: FlowConfig): FlowMedium[] {
  const fallback = config.medium ?? DEFAULTS.medium;
  const used = new Set<FlowMedium>();
  for (const route of expandRoutes(config.branches)) {
    // 첫 점은 앞에 구간이 없으므로 매질이 쓰이지 않는다
    for (let i = 1; i < route.points.length; i++) used.add(route.points[i].medium ?? fallback);
  }
  return used.size > 0 ? Array.from(used) : [fallback];
}

// ============================================================================
// 서비스 본체
// ============================================================================

interface ActiveFlow {
  node: any;
  component: any;
  config: FlowConfig;
  /**
   * 스트림이 시작된 시각 (performance.now 기준 ms).
   * 관이 어디까지 찼는지 계산해 후속 효과(거품 등)의 시작 시점을 잡는 데 쓴다.
   * 같은 id 로 다시 addFlow 해도 노드를 재생성하지 않으므로 이 값은 유지된다.
   */
  startedAt: number;
  /** 본선(root 갈래) 경로 길이 (m) */
  mainLength: number;
}

export class FlowEffectService {
  private static instance: FlowEffectService;
  private sdk: any = null;
  private flows: Map<string, ActiveFlow> = new Map();
  /**
   * 생성이 진행 중인 flow id.
   * createNode() 를 await 하는 사이에 같은 id 로 다시 불리면 노드가 두 개 생긴다.
   * 운영 앱은 모니터링 데이터가 올 때마다 addFlow 를 재호출하므로 반드시 필요하다.
   */
  private creating: Set<string> = new Set();
  private componentRegistered = false;

  static getInstance(): FlowEffectService {
    if (!FlowEffectService.instance) {
      FlowEffectService.instance = new FlowEffectService();
    }
    return FlowEffectService.instance;
  }

  /**
   * SDK 핸들 주입 + 커스텀 컴포넌트 등록. componentDidMount 시점에 1회 호출.
   */
  async initialize(sdk: any): Promise<void> {
    this.sdk = sdk;

    if (!this.componentRegistered) {
      await sdk.Scene.register(FLOW_COMPONENT_TYPE, mediaFlowFactory);
      this.componentRegistered = true;
      console.log('[FlowEffectService] mp.mediaFlow 컴포넌트 등록 완료');
    }
  }

  /**
   * 흐름 추가. 같은 id 가 이미 있으면 노드 재생성 없이 in-place 갱신
   * (진행 중인 입자가 끊기지 않음).
   */
  async addFlow(config: FlowConfig): Promise<void> {
    if (!this.sdk) {
      console.warn('[FlowEffectService] initialize() 미호출 — flow 추가 불가');
      return;
    }
    const anchor = this.anchorOf(config);
    if (!anchor) {
      console.warn(`[FlowEffectService] '${config.id}' — 본선 점이 2개 미만이라 건너뜀`);
      return;
    }

    const existing = this.flows.get(config.id);
    if (existing) {
      this.applyInputsInPlace(existing.component, config);
      existing.node.position.set(anchor.x, anchor.y, anchor.z);
      existing.config = config;
      return;
    }

    // 생성이 진행 중이면 스킵 — 매 tick 재호출 시 await 사이에 중복 노드가 생기는 것을 막는다
    if (this.creating.has(config.id)) return;
    this.creating.add(config.id);

    try {
      const node = await this.sdk.Scene.createNode();
      node.position.set(anchor.x, anchor.y, anchor.z);

      const inputs = this.buildInputs(config);
      const component = node.addComponent(FLOW_COMPONENT_TYPE, inputs);
      node.start();

      this.flows.set(config.id, {
        node, component, config,
        startedAt: nowMs(),
        mainLength: mainRouteLength(config.branches),
      });
      console.log(
        `[FlowEffectService] flow '${config.id}' 생성 — 갈래 ${config.branches.length}개, 경로 ${inputs.routes.length}개`,
      );
    } catch (error) {
      console.error('[FlowEffectService] flow 생성 실패:', error);
    } finally {
      this.creating.delete(config.id);
    }
  }

  /** 본선 시작점 = 노드 앵커. 본선 점이 2개 미만이면 null */
  private anchorOf(config: FlowConfig): Vec3 | null {
    const root = config.branches?.[0];
    if (!root || root.waypoints.length < 2) return null;
    return root.waypoints[0];
  }

  /**
   * 노드는 본선 시작점에 앵커되고 컴포넌트는 로컬 좌표로 그린다.
   * → 펼친 경로들을 앵커 기준 상대 좌표로 변환하고, 구간별 매질을 함께 실어 넘긴다.
   */
  private buildInputs(config: FlowConfig): FlowInputs {
    const anchor = this.anchorOf(config) ?? { x: 0, y: 0, z: 0 };
    const fallback = config.medium ?? DEFAULTS.medium;

    const routes: RouteInput[] = expandRoutes(config.branches).map(route => ({
      points: route.points.map(p => ({ x: p.x - anchor.x, y: p.y - anchor.y, z: p.z - anchor.z })),
      // 구간 i(점 i → 점 i+1)의 매질 = 그 구간이 '도착하는' 점(i+1번)의 매질
      mediums: route.points.slice(1).map(p => p.medium ?? fallback),
      reversed: route.reversed,
      startOffset: route.startOffset,
    }));

    return {
      routes,
      styles: config.styles ?? defaultStyles(),
      spacing: config.spacing ?? DEFAULTS.spacing,
      speed: config.speed ?? DEFAULTS.speed,
      alwaysOnTop: config.alwaysOnTop ?? DEFAULTS.alwaysOnTop,
      guide: config.guide ?? DEFAULTS.guide,
      guideWidth: config.guideWidth ?? DEFAULTS.guideWidth,
      guideOutline: config.guideOutline ?? DEFAULTS.guideOutline,
    };
  }

  /**
   * 컴포넌트 inputs 를 in-place 갱신 → SDK 가 onInputsUpdated 자동 호출.
   * routes/styles 는 새 객체를 대입해야 참조가 바뀌어 갱신이 감지된다.
   */
  private applyInputsInPlace(component: any, config: FlowConfig) {
    const inputs = this.buildInputs(config);
    component.inputs.routes = inputs.routes;
    component.inputs.styles = inputs.styles;
    component.inputs.spacing = inputs.spacing;
    component.inputs.speed = inputs.speed;
    component.inputs.alwaysOnTop = inputs.alwaysOnTop;
    component.inputs.guide = inputs.guide;
    component.inputs.guideWidth = inputs.guideWidth;
    component.inputs.guideOutline = inputs.guideOutline;
  }

  /**
   * config 를 통째로 교체하고 3D 에 반영. 모든 편집 함수의 공통 종착점.
   * 본선 점이 2개 미만이 되는 변경은 거부한다.
   */
  private commit(id: string, next: FlowConfig): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    const anchor = this.anchorOf(next);
    if (!anchor) {
      console.warn('[FlowEffectService] 본선은 점이 최소 2개 필요 — 변경 취소');
      return false;
    }
    this.applyInputsInPlace(f.component, next);
    f.node.position.set(anchor.x, anchor.y, anchor.z);
    f.config = next;
    return true;
  }

  // --------------------------------------------------------------------------
  // 파라미터
  // --------------------------------------------------------------------------

  /** 활성 flow 전체에 부분 파라미터 갱신. 갈래 구조는 유지. */
  updateAllInputs(partial: Partial<Omit<FlowConfig, 'id' | 'branches'>>): void {
    for (const id of Array.from(this.flows.keys())) {
      this.updateInputs(id, partial);
    }
  }

  /** 특정 flow 에만 부분 파라미터 갱신. */
  updateInputs(id: string, partial: Partial<Omit<FlowConfig, 'id'>>): void {
    const f = this.flows.get(id);
    if (!f) return;
    this.commit(id, { ...f.config, ...partial });
  }

  /** 매질 하나의 표현 설정만 부분 갱신 (다른 매질은 건드리지 않음) */
  updateStyle(id: string, medium: FlowMedium, partial: Partial<FlowMediumStyle>): void {
    const f = this.flows.get(id);
    if (!f) return;
    const styles = f.config.styles ?? defaultStyles();
    this.commit(id, {
      ...f.config,
      styles: { ...styles, [medium]: { ...styles[medium], ...partial } } as Record<FlowMedium, FlowMediumStyle>,
    });
  }

  // --------------------------------------------------------------------------
  // 갈래(branch) 조작
  // --------------------------------------------------------------------------

  /** 갈래 목록 (읽기 전용 사본) */
  listBranches(id: string): FlowBranch[] {
    return this.flows.get(id)?.config.branches.map(cloneBranch) ?? [];
  }

  getBranch(id: string, branchId: string): FlowBranch | null {
    const b = this.flows.get(id)?.config.branches.find(x => x.id === branchId);
    return b ? cloneBranch(b) : null;
  }

  /**
   * 부모 갈래의 pointIndex 번 점에서 갈라지는 새 갈래를 만든다.
   * 새 갈래는 firstPoint 한 점으로 시작한다 (분기점 → firstPoint 구간이 생김).
   * 반환값: 생성된 갈래 id (실패 시 null).
   */
  addBranch(id: string, parentBranchId: string, pointIndex: number, firstPoint: FlowPoint): string | null {
    const f = this.flows.get(id);
    if (!f) return null;
    const parent = f.config.branches.find(b => b.id === parentBranchId);
    if (!parent) return null;

    let n = f.config.branches.length;
    let branchId = `br-${n}`;
    while (f.config.branches.some(b => b.id === branchId)) {
      n += 1;
      branchId = `br-${n}`;
    }

    const clampedIndex = Math.max(0, Math.min(pointIndex, parent.waypoints.length - 1));
    const next: FlowConfig = {
      ...f.config,
      branches: [
        ...f.config.branches.map(cloneBranch),
        {
          id: branchId,
          label: `갈래 ${f.config.branches.length}`,
          waypoints: [{ ...firstPoint }],
          parent: { branchId: parentBranchId, pointIndex: clampedIndex },
        },
      ],
    };
    return this.commit(id, next) ? branchId : null;
  }

  /** 갈래 제거 — 그 갈래에 매달린 하위 갈래들도 함께 제거한다. 본선은 제거 불가. */
  removeBranch(id: string, branchId: string): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    const target = f.config.branches.find(b => b.id === branchId);
    if (!target || !target.parent) return false; // 본선은 제거 불가

    const doomed = new Set<string>([branchId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const b of f.config.branches) {
        if (b.parent && doomed.has(b.parent.branchId) && !doomed.has(b.id)) {
          doomed.add(b.id);
          grew = true;
        }
      }
    }

    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.filter(b => !doomed.has(b.id)).map(cloneBranch),
    });
  }

  /** 갈래가 부모의 어느 점에서 갈라지는지 변경 */
  setBranchJunction(id: string, branchId: string, pointIndex: number): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    const target = f.config.branches.find(b => b.id === branchId);
    if (!target || !target.parent) return false;
    const parent = f.config.branches.find(b => b.id === target.parent!.branchId);
    if (!parent) return false;

    const clamped = Math.max(0, Math.min(pointIndex, parent.waypoints.length - 1));
    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b =>
        b.id === branchId
          ? { ...cloneBranch(b), parent: { branchId: b.parent!.branchId, pointIndex: clamped } }
          : cloneBranch(b),
      ),
    });
  }

  updateBranchLabel(id: string, branchId: string, label: string): void {
    const f = this.flows.get(id);
    if (!f) return;
    f.config = {
      ...f.config,
      branches: f.config.branches.map(b => (b.id === branchId ? { ...cloneBranch(b), label } : cloneBranch(b))),
    };
  }

  // --------------------------------------------------------------------------
  // 점(waypoint) 조작
  // --------------------------------------------------------------------------

  /** 특정 갈래의 waypoint 배열 전체 교체 */
  setWaypoints(id: string, branchId: string, waypoints: FlowPoint[]): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b =>
        b.id === branchId ? { ...cloneBranch(b), waypoints } : cloneBranch(b),
      ),
    });
  }

  /** 점 하나 이동 (위치 조정 ± / 직접 입력 / 현위치). 매질 등 다른 속성은 유지. */
  moveWaypoint(id: string, branchId: string, index: number, position: Vec3): boolean {
    const b = this.flows.get(id)?.config.branches.find(x => x.id === branchId);
    if (!b || index < 0 || index >= b.waypoints.length) return false;
    const next = b.waypoints.map((p, i) =>
      i === index ? { ...p, x: position.x, y: position.y, z: position.z } : { ...p },
    );
    return this.setWaypoints(id, branchId, next);
  }

  /**
   * 점의 매질 지정 — 이 점에서 다음 점까지의 구간에 적용된다.
   * undefined 를 주면 흐름 기본 매질을 따르도록 되돌린다.
   */
  setPointMedium(id: string, branchId: string, index: number, medium: FlowMedium | undefined): boolean {
    const b = this.flows.get(id)?.config.branches.find(x => x.id === branchId);
    if (!b || index < 0 || index >= b.waypoints.length) return false;
    const next = b.waypoints.map((p, i) => {
      if (i !== index) return { ...p };
      const copy: FlowPoint = { x: p.x, y: p.y, z: p.z };
      if (medium) copy.medium = medium;
      return copy;
    });
    return this.setWaypoints(id, branchId, next);
  }

  /**
   * index 위치에 점 삽입 (index === length 면 끝에 추가).
   * 이 갈래를 부모로 삼는 자식들의 분기점 인덱스도 함께 밀어 준다.
   */
  insertWaypoint(id: string, branchId: string, index: number, position: FlowPoint): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    const target = f.config.branches.find(b => b.id === branchId);
    if (!target) return false;

    const at = Math.max(0, Math.min(index, target.waypoints.length));
    const nextPoints = target.waypoints.map(p => ({ ...p }));
    // 쪼개지는 구간의 매질을 물려받아 구간을 나눠도 매질이 끊기지 않게 한다.
    // 매질은 '도착 점' 이 쥐고 있으므로 원래 뒤쪽 점(= 삽입 위치의 기존 점)에서 가져온다.
    const inherited = (target.waypoints[at] ?? target.waypoints[at - 1])?.medium;
    nextPoints.splice(at, 0, inherited ? { ...position, medium: inherited } : { ...position });

    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b => {
        const c = cloneBranch(b);
        if (c.id === branchId) c.waypoints = nextPoints;
        // 삽입 지점 이상을 가리키던 자식 분기점은 한 칸 뒤로
        if (c.parent && c.parent.branchId === branchId && c.parent.pointIndex >= at) {
          c.parent = { ...c.parent, pointIndex: c.parent.pointIndex + 1 };
        }
        return c;
      }),
    });
  }

  /**
   * 점 제거. 다음 경우는 거부한다.
   *  - 본선이 2개 미만이 되는 경우
   *  - 갈래가 0개가 되는 경우
   *  - 그 점을 분기점으로 쓰는 자식 갈래가 있는 경우 (먼저 분기점을 옮기거나 갈래를 지워야 함)
   */
  removeWaypoint(id: string, branchId: string, index: number): { ok: boolean; reason?: string } {
    const f = this.flows.get(id);
    if (!f) return { ok: false, reason: '흐름을 찾을 수 없습니다.' };
    const target = f.config.branches.find(b => b.id === branchId);
    if (!target) return { ok: false, reason: '갈래를 찾을 수 없습니다.' };

    const isRoot = !target.parent;
    const min = isRoot ? 2 : 1;
    if (target.waypoints.length <= min) {
      return { ok: false, reason: isRoot ? '본선은 점이 최소 2개 필요합니다.' : '갈래는 점이 최소 1개 필요합니다.' };
    }

    const users = f.config.branches.filter(
      b => b.parent && b.parent.branchId === branchId && b.parent.pointIndex === index,
    );
    if (users.length > 0) {
      const names = users.map(b => b.label ?? b.id).join(', ');
      return { ok: false, reason: `이 점을 분기점으로 쓰는 갈래가 있습니다: ${names}` };
    }

    const nextPoints = target.waypoints.filter((_, i) => i !== index).map(p => ({ ...p }));
    const ok = this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b => {
        const c = cloneBranch(b);
        if (c.id === branchId) c.waypoints = nextPoints;
        // 제거 지점 뒤를 가리키던 자식 분기점은 한 칸 앞으로
        if (c.parent && c.parent.branchId === branchId && c.parent.pointIndex > index) {
          c.parent = { ...c.parent, pointIndex: c.parent.pointIndex - 1 };
        }
        return c;
      }),
    });
    return ok ? { ok: true } : { ok: false, reason: '변경이 거부되었습니다.' };
  }

  /** 흐름 전체(모든 갈래의 모든 점)를 delta 만큼 평행이동 */
  translateFlow(id: string, delta: Vec3): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b => ({
        ...cloneBranch(b),
        waypoints: b.waypoints.map(p => ({ ...p, x: p.x + delta.x, y: p.y + delta.y, z: p.z + delta.z })),
      })),
    });
  }

  /**
   * 갈래 하나의 흐름 방향 반전.
   * 진행거리를 뒤집어 읽는 방식이라 풀 재생성 없이 즉시 반영되고 입자가 끊기지 않는다.
   */
  toggleBranchReversed(id: string, branchId: string): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b =>
        b.id === branchId ? { ...cloneBranch(b), reversed: !(b.reversed ?? false) } : cloneBranch(b),
      ),
    });
  }

  /** 모든 갈래의 방향을 한 번에 뒤집는다 */
  toggleAllReversed(id: string): boolean {
    const f = this.flows.get(id);
    if (!f) return false;
    return this.commit(id, {
      ...f.config,
      branches: f.config.branches.map(b => ({ ...cloneBranch(b), reversed: !(b.reversed ?? false) })),
    });
  }

  /** 실제로 경로를 만들어 내는 갈래 id 목록 — 방향 버튼을 보여줄 대상 */
  routeBranchIds(id: string): string[] {
    const f = this.flows.get(id);
    return f ? expandRoutes(f.config.branches).map(r => r.branchId) : [];
  }

  // --------------------------------------------------------------------------
  // 흐름 목록 / 저장
  // --------------------------------------------------------------------------

  removeFlow(id: string): void {
    const f = this.flows.get(id);
    if (!f) return;
    try {
      f.node.stop();
    } catch (_) { /* noop */ }
    this.flows.delete(id);
  }

  clear(): void {
    for (const id of Array.from(this.flows.keys())) {
      this.removeFlow(id);
    }
  }

  listFlowIds(): string[] {
    return Array.from(this.flows.keys());
  }

  getConfig(id: string): FlowConfig | null {
    const f = this.flows.get(id);
    return f ? cloneConfig(f.config) : null;
  }

  getAllConfigs(): FlowConfig[] {
    return Array.from(this.flows.values()).map(f => cloneConfig(f.config));
  }

  /**
   * 본선(root 갈래)이 끝까지 채워졌는지.
   *
   * 첫 재생에서는 관이 시작점부터 차례로 채워지므로, 물이 본선을 다 지나간 뒤에
   * 장비가 반응하도록 거품 같은 후속 효과의 시작 시점으로 쓴다.
   * 컴포넌트 내부 시계를 들여다보지 않고 경과 시간 × 속도로 계산한다 —
   * 프레임 단위 오차는 있어도 UI 를 여닫는 판단에는 충분하다.
   */
  isMainFilled(id: string): boolean {
    const f = this.flows.get(id);
    if (!f || f.mainLength <= 0) return false;
    const speed = f.config.speed ?? DEFAULTS.speed;
    if (speed <= 0) return false;
    return ((nowMs() - f.startedAt) / 1000) * speed >= f.mainLength;
  }

  /** 본선이 다 차기까지 남은 시간(초). 이미 찼으면 0 */
  remainingFillSeconds(id: string): number {
    const f = this.flows.get(id);
    if (!f || f.mainLength <= 0) return 0;
    const speed = f.config.speed ?? DEFAULTS.speed;
    if (speed <= 0) return 0;
    const travelled = ((nowMs() - f.startedAt) / 1000) * speed;
    return Math.max(0, (f.mainLength - travelled) / speed);
  }

  /** 흐름의 경로 개수 — 분기 상태를 UI 에 보여주기 위한 값 */
  routeCount(id: string): number {
    const f = this.flows.get(id);
    return f ? expandRoutes(f.config.branches).length : 0;
  }

  /** label 만 갱신. 3D 동작에는 영향 없고 저장 JSON 에만 반영. */
  updateLabel(id: string, label: string): void {
    const f = this.flows.get(id);
    if (!f) return;
    f.config = { ...f.config, label };
  }

  /** id 변경. Map 키 + config.id 만 갱신, 노드는 재생성하지 않음. */
  renameId(oldId: string, newId: string): boolean {
    if (oldId === newId) return true;
    if (!this.flows.has(oldId)) return false;
    if (this.flows.has(newId)) {
      console.warn(`[FlowEffectService] '${newId}' 가 이미 사용 중 — 변경 취소`);
      return false;
    }
    const f = this.flows.get(oldId)!;
    f.config = { ...f.config, id: newId };
    this.flows.delete(oldId);
    this.flows.set(newId, f);
    return true;
  }

  /** 선택된 흐름을 갈래 구조까지 통째로 복제. X축으로 살짝 오프셋. */
  async duplicateFlow(sourceId: string, xOffset = 0.2): Promise<string | null> {
    const f = this.flows.get(sourceId);
    if (!f) return null;

    let newId = `${sourceId}-copy`;
    let n = 2;
    while (this.flows.has(newId)) {
      newId = `${sourceId}-copy-${n}`;
      n += 1;
    }

    await this.addFlow({
      ...cloneConfig(f.config),
      id: newId,
      label: f.config.label ? `${f.config.label} 사본` : undefined,
      branches: f.config.branches.map(b => ({
        ...cloneBranch(b),
        waypoints: b.waypoints.map(p => ({ ...p, x: p.x + xOffset })),
      })),
    });
    return newId;
  }
}

/** 컴포넌트의 자체 clock 과 같은 기준을 쓴다 */
function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** 본선(root 갈래)이 만들어 내는 경로의 길이 (m) */
function mainRouteLength(branches: FlowBranch[]): number {
  const root = branches.find(b => !b.parent);
  if (!root) return 0;
  const route = expandRoutes(branches).find(r => r.branchId === root.id);
  return route ? polylineLength(route.points, route.points.length - 1) : 0;
}

// --- 깊은 복사 유틸 ----------------------------------------------------------

function cloneBranch(b: FlowBranch): FlowBranch {
  return {
    ...b,
    waypoints: b.waypoints.map(p => ({ ...p })),
    parent: b.parent ? { ...b.parent } : undefined,
  };
}

function cloneConfig(c: FlowConfig): FlowConfig {
  return {
    ...c,
    branches: c.branches.map(cloneBranch),
    styles: c.styles
      ? { water: { ...c.styles.water }, air: { ...c.styles.air } }
      : defaultStyles(),
  };
}
